import React, { type Dispatch, type SetStateAction } from 'react';
import {
  type TrackToggleProps,
  useParticipants,
  useRoomContext,
  useTrackToggle,
} from '@livekit/components-react';
import { Mic, MicOff, Video, VideoOff } from 'lucide-react';
import { Track } from 'livekit-client';
import type { ToggleSource } from '@livekit/components-core';
import { useAdminTrackControl } from '@/hooks/useAdminTrackControl';
import { useMediaDevices } from '@/hooks/useMediaDevices';
import { usePretestDevices, usePretestDevicesSetSelection } from '@/contexts/PretestDevicesContext';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useWebinarT } from '@/hooks/useWebinarT';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CustomTrackToggleProps<T extends ToggleSource> extends TrackToggleProps<T> {
  showIcon?: boolean;
  isAdmin?: boolean;
  /** Intervenant lien public : mêmes réglages locaux que le présentateur (sans API mute distant). */
  isSpeaker?: boolean;
  participantIdentity?: string;
  roomName?: string;
  workspaceId?: number;
  webinarId?: number;
  sessionId?: number;
  cameraEnabled?: boolean;
  microphoneEnabled?: boolean;
  onLocalCameraEnabledChange?: Dispatch<SetStateAction<boolean>>;
  onLocalMicrophoneEnabledChange?: Dispatch<SetStateAction<boolean>>;
  /**
   * Si false : allumer micro/cam ne publie pas encore sur LiveKit (intent parent seulement).
   * Pertinent pour l’intervenant pas encore « ajouté au live ».
   */
  publishLocalMediaToRoom?: boolean;
}

export const AdminCustomTrackToggle: <T extends ToggleSource>(
  props: CustomTrackToggleProps<T> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode = React.forwardRef(function CustomTrackToggle<T extends ToggleSource>(
  {
    showIcon = true,
    source,
    isAdmin,
    isSpeaker = false,
    participantIdentity,
    roomName,
    workspaceId,
    webinarId,
    sessionId,
    cameraEnabled,
    microphoneEnabled,
    onLocalCameraEnabledChange,
    onLocalMicrophoneEnabledChange,
    publishLocalMediaToRoom = true,
    ...props
  }: CustomTrackToggleProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { t } = useWebinarT('webinars');
  const room = useRoomContext();
  const { buttonProps, enabled } = useTrackToggle({ source, ...props });
  const [isClient, setIsClient] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSwitchingDevice, setIsSwitchingDevice] = React.useState(false);
  const participants = useParticipants();
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);
  const visualIdentity = currentWorkspace?.visualIdentity;
  const { audioInputs, videoInputs } = useMediaDevices();

  const isLocalParticipant =
    !!participantIdentity && participantIdentity === room.localParticipant.identity;
  const pretestDevices = usePretestDevices();
  const setPretestSelection = usePretestDevicesSetSelection();

  /** Menu cam/micro (état + devices) : admin (local ou distant) ou intervenant sur sa propre image. */
  const usesModerationDropdown = Boolean(
    participantIdentity && (isAdmin === true || (isSpeaker === true && isLocalParticipant)),
  );

  const adminTrackControl = useAdminTrackControl({
    roomName: roomName || room.name,
    participantIdentity: participantIdentity || '',
    workspaceId: workspaceId || 0,
    webinarId: webinarId || 0,
    sessionId: sessionId || 0,
  });

  const getParticipantTrackState = React.useCallback(() => {
    if (!usesModerationDropdown) {
      return enabled;
    }

    if (isLocalParticipant) {
      if (source === Track.Source.Camera && onLocalCameraEnabledChange) {
        return cameraEnabled ?? true;
      }
      if (source === Track.Source.Microphone && onLocalMicrophoneEnabledChange) {
        return microphoneEnabled ?? true;
      }
      return source === Track.Source.Camera
        ? room.localParticipant.isCameraEnabled
        : room.localParticipant.isMicrophoneEnabled;
    }

    const targetParticipant = participants.find((p) => p.identity === participantIdentity);
    if (!targetParticipant) {
      return false;
    }

    const pub = targetParticipant.getTrackPublication(source);
    return !!(pub && !pub.isMuted && pub.track);
  }, [
    usesModerationDropdown,
    isLocalParticipant,
    participantIdentity,
    room,
    participants,
    source,
    enabled,
    onLocalCameraEnabledChange,
    onLocalMicrophoneEnabledChange,
    cameraEnabled,
    microphoneEnabled,
  ]);

  const actualTrackState = getParticipantTrackState();

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!usesModerationDropdown) {
      buttonProps.onClick?.(event);
      return;
    }
    // micro et caméra se pilotent via le Switch du dropdown, pas via le clic
    if (source === Track.Source.Microphone || source === Track.Source.Camera) {
      return;
    }
  };

  const applyLocalAudioAfterOn = async () => {
    const id =
      pretestDevices?.microphoneId ||
      room.getActiveDevice?.('audioinput') ||
      audioInputs[0]?.deviceId ||
      undefined;
    if (id && room.switchActiveDevice) {
      try {
        await room.switchActiveDevice('audioinput', id);
      } catch (e) {
        console.error('AdminCustomTrackToggle: switchActiveDevice audioinput', e);
      }
    }
  };

  const applyLocalVideoAfterOn = async () => {
    const id =
      pretestDevices?.cameraId ||
      room.getActiveDevice?.('videoinput') ||
      videoInputs[0]?.deviceId ||
      undefined;
    if (id && room.switchActiveDevice) {
      try {
        await room.switchActiveDevice('videoinput', id);
      } catch (e) {
        console.error('AdminCustomTrackToggle: switchActiveDevice videoinput', e);
      }
    }
  };

  const handleMicrophoneSwitchChange = async (checked: boolean) => {
    if (!participantIdentity) return;

    if (isLocalParticipant && (isAdmin || isSpeaker === true)) {
      if (!onLocalMicrophoneEnabledChange) return;
      setIsLoading(true);
      try {
        if (checked && publishLocalMediaToRoom !== false) {
          await room.localParticipant.setMicrophoneEnabled(true);
          await applyLocalAudioAfterOn();
        } else if (!checked) {
          await room.localParticipant.setMicrophoneEnabled(false);
        }
        onLocalMicrophoneEnabledChange(checked);
      } catch (error) {
        console.error('Failed to toggle microphone:', error);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!isAdmin) return;
    setIsLoading(true);
    try {
      if (isLocalParticipant) {
        await room.localParticipant.setMicrophoneEnabled(checked);
        if (checked) {
          await applyLocalAudioAfterOn();
        }
      } else {
        await adminTrackControl.setMicrophoneEnabled(checked);
      }
    } catch (error) {
      console.error('Failed to toggle microphone:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCameraSwitchChange = async (checked: boolean) => {
    if (!participantIdentity) return;

    if (isLocalParticipant && (isAdmin || isSpeaker === true)) {
      if (!onLocalCameraEnabledChange) return;
      setIsLoading(true);
      try {
        if (checked && publishLocalMediaToRoom !== false) {
          await room.localParticipant.setCameraEnabled(true);
          await applyLocalVideoAfterOn();
        } else if (!checked) {
          await room.localParticipant.setCameraEnabled(false);
        }
        onLocalCameraEnabledChange(checked);
      } catch (error) {
        console.error('Failed to toggle camera:', error);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!isAdmin) return;
    setIsLoading(true);
    try {
      if (isLocalParticipant) {
        await room.localParticipant.setCameraEnabled(checked);
        if (checked) {
          await applyLocalVideoAfterOn();
        }
      } else {
        await adminTrackControl.setCameraEnabled(checked);
      }
    } catch (error) {
      console.error('Failed to toggle camera:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const activeAudioDeviceId = room.getActiveDevice?.('audioinput') ?? '';
  const activeVideoDeviceId = room.getActiveDevice?.('videoinput') ?? '';

  const patchPretestCameraId = React.useCallback(
    (cameraId: string) => {
      setPretestSelection?.((prev) =>
        prev
          ? { ...prev, cameraId }
          : {
              cameraId,
              microphoneId: audioInputs[0]?.deviceId ?? '',
              audioOutputId: '',
              cameraEnabled: cameraEnabled ?? true,
              microphoneEnabled: microphoneEnabled ?? true,
            },
      );
    },
    [cameraEnabled, microphoneEnabled, audioInputs],
  );

  const patchPretestMicrophoneId = React.useCallback(
    (microphoneId: string) => {
      setPretestSelection?.((prev) =>
        prev
          ? { ...prev, microphoneId }
          : {
              cameraId: videoInputs[0]?.deviceId ?? '',
              microphoneId,
              audioOutputId: '',
              cameraEnabled: cameraEnabled ?? true,
              microphoneEnabled: microphoneEnabled ?? true,
            },
      );
    },
    [cameraEnabled, microphoneEnabled, videoInputs],
  );

  const handleMicrophoneDeviceChange = async (deviceId: string) => {
    patchPretestMicrophoneId(deviceId);
    if (!room.switchActiveDevice || !deviceId) return;
    setIsSwitchingDevice(true);
    try {
      await room.switchActiveDevice('audioinput', deviceId);
    } catch (error) {
      console.error('Failed to switch microphone:', error);
    } finally {
      setIsSwitchingDevice(false);
    }
  };

  const handleCameraDeviceChange = async (deviceId: string) => {
    patchPretestCameraId(deviceId);
    if (!room.switchActiveDevice || !deviceId) return;
    setIsSwitchingDevice(true);
    try {
      await room.switchActiveDevice('videoinput', deviceId);
    } catch (error) {
      console.error('Failed to switch camera:', error);
    } finally {
      setIsSwitchingDevice(false);
    }
  };

  const getIcon = () => {
    if (!showIcon) return null;

    const trackEnabled = usesModerationDropdown ? actualTrackState : enabled;
    const iconColor = visualIdentity?.theme === 'DARK' ? 'text-black' : 'text-white';

    switch (source) {
      case Track.Source.Microphone:
        return trackEnabled ? (
          <Mic className={cn('size-5', iconColor)} />
        ) : (
          <MicOff className="size-5 text-[#FFC609]" />
        );
      case Track.Source.Camera:
        return trackEnabled ? (
          <Video className={cn('size-5', iconColor)} />
        ) : (
          <VideoOff className="size-5 text-[#FFC609]" />
        );
      default:
        return null;
    }
  };

  return (
    isClient && (
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="border-none outline-none">
          <button
            ref={ref}
            onClick={handleClick}
            disabled={isLoading || adminTrackControl.isLoading}
            className={cn(
              'p-2 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              visualIdentity?.theme === 'DARK' ? 'bg-white' : 'bg-dark-lighter',
            )}
          >
            {getIcon()}
            {props.children}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-none outline-none p-2">
          {source === Track.Source.Microphone && (
            <>
              <div className="flex items-center justify-between gap-4 px-2 py-1.5">
                <Label htmlFor="admin-mic-switch">
                  {actualTrackState
                    ? t('adminLive.trackToggle.disableMic')
                    : t('adminLive.trackToggle.enableMic')}
                </Label>
                <Switch
                  id="admin-mic-switch"
                  checked={actualTrackState}
                  onCheckedChange={handleMicrophoneSwitchChange}
                  disabled={isLoading || adminTrackControl.isLoading}
                />
              </div>
              {isLocalParticipant && audioInputs.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-2 pt-1.5">
                  <Label htmlFor="admin-mic-device">{t('adminLive.trackToggle.chooseMic')}</Label>
                  <Select
                    value={
                      activeAudioDeviceId ||
                      pretestDevices?.microphoneId ||
                      audioInputs[0]?.deviceId ||
                      ''
                    }
                    onValueChange={handleMicrophoneDeviceChange}
                    disabled={isSwitchingDevice}
                  >
                    <SelectTrigger
                      id="admin-mic-device"
                      className="text-base px-2 h-fit py-1 gap-1 justify-center w-fit"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {audioInputs.map((device) => (
                        <SelectItem key={device.deviceId} value={device.deviceId}>
                          {device.label ||
                            t('adminLive.trackToggle.micFallback', {
                              id: device.deviceId.slice(0, 8),
                            })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}
          {source === Track.Source.Camera && (
            <>
              <div className="flex items-center justify-between gap-4 px-2 py-1.5">
                <Label htmlFor="admin-camera-switch">
                  {actualTrackState
                    ? t('adminLive.trackToggle.disableCamera')
                    : t('adminLive.trackToggle.enableCamera')}
                </Label>
                <Switch
                  id="admin-camera-switch"
                  checked={actualTrackState}
                  onCheckedChange={handleCameraSwitchChange}
                  disabled={isLoading || adminTrackControl.isLoading}
                />
              </div>
              {isLocalParticipant && videoInputs.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-2 pt-1.5">
                  <Label htmlFor="admin-camera-device">
                    {t('adminLive.trackToggle.chooseCamera')}
                  </Label>
                  <Select
                    value={
                      activeVideoDeviceId ||
                      pretestDevices?.cameraId ||
                      videoInputs[0]?.deviceId ||
                      ''
                    }
                    onValueChange={handleCameraDeviceChange}
                    disabled={isSwitchingDevice}
                  >
                    <SelectTrigger
                      id="admin-camera-device"
                      className="text-base px-2 h-fit py-1 gap-1 justify-center w-fit  "
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {videoInputs.map((device) => (
                        <SelectItem key={device.deviceId} value={device.deviceId}>
                          {device.label ||
                            t('adminLive.trackToggle.cameraFallback', {
                              id: device.deviceId.slice(0, 8),
                            })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  );
});
